/**
 * DEENEX Activa — datos seed del mockup (marca demo: HATSU 🍣).
 *
 * Números elegidos para ser coherentes entre sí y para maximizar el "WOW".
 * NO son reales: alimentan el prototipo navegable en modo mock.
 */

import type {
  Grupo,
  MapeoColumna,
  ReporteComercial,
  Resultados,
  CampanaNueva,
  CalidadDatos,
  LineaWhatsApp,
  ResultadoGrupo,
  Respuesta,
  NoContactar,
  HistorialCampana,
} from "./types";

export const MARCA_DEMO = "HATSU";

export const campanaDemo: CampanaNueva = {
  id: "act_hatsu_001",
  marca: MARCA_DEMO,
  estado: "borrador",
  creada: "2026-07-12",
  contactos: 21540,
  grupos: 4,
};

/** Paso 2 — mapeo de columnas (dos archivos, con match propuesto). */
export const mapeoDemo: MapeoColumna[] = [
  // Archivo de clientes
  { archivo: "clientes", columnaCliente: "email_cliente", campoDeenex: "Mail", obligatorio: true, detectado: true, ejemplo: "juan@gmail.com" },
  { archivo: "clientes", columnaCliente: "id_cli", campoDeenex: "ID del cliente", obligatorio: true, detectado: true, ejemplo: "C-10482" },
  { archivo: "clientes", columnaCliente: "Celular", campoDeenex: "Teléfono", obligatorio: false, detectado: true, ejemplo: "+54 9 11 5544-3322" },
  { archivo: "clientes", columnaCliente: "Nombre", campoDeenex: "Nombre", obligatorio: false, detectado: true, ejemplo: "Juan" },
  { archivo: "clientes", columnaCliente: "sucursal_fav", campoDeenex: "Sucursal", obligatorio: false, detectado: true, ejemplo: "Palermo" },
  { archivo: "clientes", columnaCliente: "acepta_promos", campoDeenex: "Opt-in (consentimiento)", obligatorio: false, detectado: true, ejemplo: "sí" },
  // Archivo de pedidos
  { archivo: "pedidos", columnaCliente: "id_cli", campoDeenex: "ID del cliente", obligatorio: true, detectado: true, ejemplo: "C-10482" },
  { archivo: "pedidos", columnaCliente: "Fecha_Orden", campoDeenex: "Fecha de la orden", obligatorio: true, detectado: true, ejemplo: "2025-11-03" },
  { archivo: "pedidos", columnaCliente: "Importe_Total", campoDeenex: "Valor del pedido", obligatorio: true, detectado: true, ejemplo: "$ 12.400" },
  { archivo: "pedidos", columnaCliente: "canal_venta", campoDeenex: "Canal de venta", obligatorio: false, detectado: true, ejemplo: "Delivery" },
];

export const reporteDemo: ReporteComercial = {
  marca: MARCA_DEMO,
  plataDormida: 419_000_000,
  baseTotalHistorica: 890_000_000, // (a) contexto de tamaño: total facturado en la historia (NO "dormido")
  tasaRecuperoConservadora: 0.03,  // (c) piso conservador para el recuperable estimado
  concentracion: { pctClientes: 8, pctFacturacion: 47 },
  baseDormida: { pct: 71, meses: 6 },
  totalClientes: 21540,
  rotativo: {
    titulo: "Tu mejor día es el viernes",
    valor: "el 34% de tu facturación",
    detalle:
      "Los viernes concentran más ventas que sábado y domingo juntos. Un buen momento para lanzar campañas.",
  },
};

/** Paso 3/5/6 — los 4 grupos + el mensaje armado por grupo (determinístico, sin IA por ahora). */
export const gruposDemo: Grupo[] = [
  {
    id: "g_dormidos_alto",
    nombre: "Dormidos de alto valor",
    emoji: "💎",
    accent: { bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200", dot: "bg-violet-500" },
    personas: 1726,
    valor: 180_000_000,
    perfil: "Gastaban mucho y hace más de 6 meses que no vuelven. Oro puro para reactivar.",
    campana: "Rescate",
    oferta: "Cupón 20% off",
    canal: "WhatsApp",
    prioridad: "Alta",
    descuentoPct: 20,
    mensaje:
      "Hola {nombre}, te extrañamos en HATSU 🍣 Volvé con un 20% off en tu próximo pedido, solo por esta semana.",
  },
  {
    id: "g_campeones",
    nombre: "Campeones",
    emoji: "🏆",
    accent: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", dot: "bg-amber-500" },
    personas: 890,
    valor: 210_000_000,
    perfil: "Tus mejores clientes activos. Compran seguido y gastan alto. Hay que cuidarlos.",
    campana: "Fidelización",
    oferta: "Beneficio VIP (sin descuento)",
    canal: "WhatsApp + Email",
    prioridad: "Alta",
    descuentoPct: 0,
    mensaje:
      "Hola {nombre}, sos de los que más nos eligen 🙏 Te reservamos un beneficio VIP para tu próximo pedido en HATSU.",
  },
  {
    id: "g_nuevos",
    nombre: "Clientes nuevos",
    emoji: "🌱",
    accent: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500" },
    personas: 3400,
    valor: 45_000_000,
    perfil: "Compraron una sola vez, hace poco. La segunda compra define si se quedan.",
    campana: "Bienvenida",
    oferta: "Cupón 15% off",
    canal: "Email",
    prioridad: "Media",
    descuentoPct: 15,
    mensaje:
      "Hola {nombre}, gracias por tu primera compra 🙌 Acá va un 15% para que vuelvas a probar tus rolls favoritos.",
  },
  {
    id: "g_riesgo",
    nombre: "En riesgo",
    emoji: "⚠️",
    accent: { bg: "bg-orange-50", text: "text-orange-700", ring: "ring-orange-200", dot: "bg-orange-500" },
    personas: 2100,
    valor: 95_000_000,
    perfil: "Compraban seguido y empezaron a enfriarse. Ventana corta para retenerlos.",
    campana: "Reactivación",
    oferta: "Cupón 15% off",
    canal: "WhatsApp",
    prioridad: "Media",
    descuentoPct: 15,
    mensaje:
      "Hola {nombre}, hace un tiempo no te vemos 🍱 Volvé con 15% off esta semana y date un gusto en HATSU.",
  },
];

/** Paso 8 — resultados de la campaña de "Dormidos de alto valor". */
export const resultadosDemo: Resultados = {
  enviados: 1726,
  entregados: 1683,
  respondieron: 214,
  bajas: 19,
  volvieron: 168,
  plataRecuperada: 8_240_000,
};

/** Paso 2 — resumen de calidad de datos tras el cruce (casos especiales §5). */
export const calidadDatos: CalidadDatos = {
  totalClientes: 21540,
  sinContacto: 1240,
  duplicadosCelular: 312,
  mesesHistoria: 14,
  pocaHistoria: false,
};

/** Paso 7 — líneas de WhatsApp conectadas (multi-número §4.bis). */
export const lineasDemo: LineaWhatsApp[] = [
  { id: "l1", nombre: "HATSU Palermo", estado: "activa", msgsPorDia: 200, enviadosHoy: 148 },
  { id: "l2", nombre: "HATSU Centro", estado: "activa", msgsPorDia: 200, enviadosHoy: 96 },
  { id: "l3", nombre: "HATSU Nordelta", estado: "calentando", msgsPorDia: 40, enviadosHoy: 12 },
];

/** Paso 8 — resultados por grupo (agregado §4.quater). */
export const resultadosPorGrupo: ResultadoGrupo[] = [
  { grupoId: "g_dormidos_alto", nombre: "Dormidos de alto valor", emoji: "💎", enviados: 1726, entregados: 1683, leidos: 1240, respondieron: 214, volvieron: 168, plata: 8_240_000 },
  { grupoId: "g_campeones", nombre: "Campeones", emoji: "🏆", enviados: 890, entregados: 881, leidos: 712, respondieron: 143, volvieron: 121, plata: 6_050_000 },
  { grupoId: "g_nuevos", nombre: "Clientes nuevos", emoji: "🌱", enviados: 3400, entregados: 3218, leidos: 2106, respondieron: 187, volvieron: 96, plata: 1_920_000 },
  { grupoId: "g_riesgo", nombre: "En riesgo", emoji: "⚠️", enviados: 2100, entregados: 2044, leidos: 1387, respondieron: 168, volvieron: 88, plata: 2_460_000 },
];

/** Paso 9 — respuestas entrantes con contexto de campaña (§4.ter). */
export const respuestasDemo: Respuesta[] = [
  {
    id: "r1", persona: "Juan Pérez", grupo: "Dormidos de alto valor", campana: "Rescate",
    oferta: "20% off válido esta semana", linea: "HATSU Palermo", hora: "hace 5 min",
    mensajeEnviado: "Hola Juan, te extrañamos en HATSU 🍣 Volvé con un 20% off en tu próximo pedido, solo por esta semana.",
    respuesta: "¿Hasta cuándo tengo el descuento?", estado: "sin responder",
  },
  {
    id: "r2", persona: "María Gómez", grupo: "Clientes nuevos", campana: "Bienvenida",
    oferta: "15% off primera recompra", linea: "HATSU Centro", hora: "hace 22 min",
    mensajeEnviado: "Hola María, gracias por tu primera compra 🙌 Acá va un 15% para que vuelvas a probar tus rolls favoritos.",
    respuesta: "¡Buenísimo! ¿El envío lo hacen a Caballito?", estado: "sin responder",
  },
  {
    id: "r3", persona: "Diego Fernández", grupo: "En riesgo", campana: "Reactivación",
    oferta: "15% off esta semana", linea: "HATSU Palermo", hora: "hace 1 h",
    mensajeEnviado: "Hola Diego, hace un tiempo no te vemos 🍱 Volvé con 15% off esta semana y date un gusto en HATSU.",
    respuesta: "No me escriban más por favor", estado: "baja",
  },
  {
    id: "r4", persona: "Sofía Ruiz", grupo: "Campeones", campana: "Fidelización",
    oferta: "Beneficio VIP", linea: "HATSU Centro", hora: "hace 2 h",
    mensajeEnviado: "Hola Sofía, sos de los que más nos eligen 🙏 Te reservamos un beneficio VIP para tu próximo pedido en HATSU.",
    respuesta: "Gracias! Ya hago el pedido 😍", estado: "respondida",
  },
];

/** Lista de no-contactar (§4.quater). */
export const noContactarDemo: NoContactar[] = [
  { contacto: "+54 9 11 3344-5566", canal: "WhatsApp", motivo: "Pidió baja por WhatsApp", fecha: "2026-07-11" },
  { contacto: "diego.fernandez@gmail.com", canal: "Email", motivo: "Link de baja en email", fecha: "2026-07-10" },
  { contacto: "+54 9 11 7788-9900", canal: "WhatsApp", motivo: "Pidió baja por WhatsApp", fecha: "2026-07-08" },
];

/** Portal del cliente — historial de campañas (§3). */
export const historialDemo: HistorialCampana[] = [
  { id: "h1", nombre: "Reactivación de dormidos", fecha: "2026-06-12", contactados: 1726, volvieron: 168, plata: 8_240_000 },
  { id: "h2", nombre: "Bienvenida a nuevos", fecha: "2026-05-20", contactados: 3400, volvieron: 96, plata: 1_920_000 },
];

/** Helper de formato de plata (pesos argentinos, sin decimales). */
export function formatARS(n: number): string {
  return "$ " + n.toLocaleString("es-AR", { maximumFractionDigits: 0 });
}

/** Formato compacto para números grandes ($419M). */
export function formatCompact(n: number): string {
  if (n >= 1_000_000) return "$ " + (n / 1_000_000).toLocaleString("es-AR", { maximumFractionDigits: 0 }) + "M";
  if (n >= 1_000) return "$ " + (n / 1_000).toLocaleString("es-AR", { maximumFractionDigits: 0 }) + "k";
  return "$ " + n.toLocaleString("es-AR");
}
